import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, TextInput, Group, LoadingOverlay, Container } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate, useParams } from 'react-router-dom'; // Changed: removed useSearchParams, added useParams
import { useMutation, useQuery } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { UpdateMasterAreaDto } from 'services/api';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

const items = [
  { title: 'Master Area', href: '/app/dacum/master-area' },
  { title: 'Edit Area', href: '' }
];

const areaSchema = z.object({
  areaName: z.string().min(1, 'Area name is required').max(255, 'Area name is too long'),
  areaCode: z
    .string()
    .min(1, 'Area code is required')
    .max(10, 'Area code must be 10 characters or less')
});

type FormData = z.infer<typeof areaSchema>;

const EditMasterAreaPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: areaId } = useParams<{ id: string }>(); // Changed: use useParams instead of useSearchParams
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(areaSchema),
    defaultValues: {
      areaName: '',
      areaCode: ''
    }
  });

  // Fetch area details from /detail endpoint
  const { data: areaData, isLoading: isLoadingArea } = useQuery({
    queryKey: ['masterAreaDetail', areaId],
    queryFn: () => masterAreaService.getDetail(areaId!),
    enabled: !!areaId,
    select: (response) => {
      // Handle both direct data and nested response structures
      return response.data || response;
    }
  });

  // Update form when data is loaded
  useEffect(() => {
    if (areaData) {
      reset({
        areaName: areaData.areaName,
        areaCode: areaData.areaCode
      });
    }
  }, [areaData, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => {
      if (!areaId) throw new Error('Area ID is required');

      const updateDto: UpdateMasterAreaDto = {
        areaID: areaId,
        areaName: data.areaName,
        areaCode: data.areaCode
      };

      return masterAreaService.update(updateDto);
    },
    onSuccess: () => {
      navigate('/app/dacum/master-area');
      showSuccessNotification({
        title: 'Updated Successfully!',
        message: 'Your area has been updated successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to update area',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const processSubmit = () => {
    if (formData) {
      updateMutation.mutate(formData);
    }
    close();
  };

  // Redirect if no ID provided
  useEffect(() => {
    if (!areaId) {
      navigate('/app/dacum/master-area');
      showErrorNotification({
        title: 'Invalid Request',
        message: 'Area ID is required for editing'
      });
    }
  }, [areaId, navigate]);

  if (!areaId) {
    return null;
  }

  return (
    <Container fluid>
      <PageTitle title='Edit Area' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Update'
        statusProcess='edit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processSubmit}
        itemLabel='area'
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay visible={isLoadingArea || updateMutation.isPending} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap='md'>
            <Controller
              name='areaCode'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Area Code'
                  placeholder='Enter area code (e.g., JKT-SLT)'
                  error={errors.areaCode?.message}
                  required
                  description='Short code to identify the area (max 10 characters)'
                  {...field}
                />
              )}
            />

            <Controller
              name='areaName'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Area Name'
                  placeholder='Enter area name (e.g., Area Jakarta Selatan)'
                  error={errors.areaName?.message}
                  required
                  description='Full name of the area'
                  {...field}
                />
              )}
            />
          </Stack>

          <Group justify='flex-end' mt='xl'>
            <SecondaryButton
              text='Cancel'
              onClick={() => navigate('/app/dacum/master-area')}
              mr={10}
            />
            <PrimaryButton
              text='Update'
              type='submit'
              loading={isSubmitting || updateMutation.isPending}
            />
          </Group>
        </form>
      </div>
    </Container>
  );
};

export default EditMasterAreaPage;
